import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { env } from '@/lib/env'
import { isMainAppHost } from '@/lib/campaign-custom-domain'
import { fetchCampaignSlugByHost } from '@/lib/landing'

type CustomDomainStatus = 'loading' | 'main' | 'custom' | 'unknown'

type CustomDomainCampaignValue = {
  status: CustomDomainStatus
  slug: string | null
}

const CustomDomainCampaignContext = createContext<CustomDomainCampaignValue | undefined>(undefined)

export function CustomDomainCampaignProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CustomDomainStatus>('loading')
  const [slug, setSlug] = useState<string | null>(null)

  useEffect(() => {
    const hostname = window.location.hostname
    if (isMainAppHost(hostname, env.publicAppUrl)) {
      setStatus('main')
      setSlug(null)
      return
    }

    const controller = new AbortController()
    let cancelled = false

    fetchCampaignSlugByHost(hostname, controller.signal)
      .then((resolvedSlug) => {
        if (cancelled) return
        if (resolvedSlug) {
          setSlug(resolvedSlug)
          setStatus('custom')
          return
        }
        setSlug(null)
        setStatus('unknown')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        setSlug(null)
        setStatus('unknown')
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [])

  const value = useMemo(
    () => ({
      status,
      slug,
    }),
    [status, slug]
  )

  return (
    <CustomDomainCampaignContext.Provider value={value}>{children}</CustomDomainCampaignContext.Provider>
  )
}

/** Hook para o modo de domínio personalizado da campanha pública. */
// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useCustomDomainCampaign(): CustomDomainCampaignValue {
  const ctx = useContext(CustomDomainCampaignContext)
  if (!ctx) {
    throw new Error('useCustomDomainCampaign must be used within CustomDomainCampaignProvider')
  }
  return ctx
}

export function useCustomDomainSlug(): string | null {
  const ctx = useContext(CustomDomainCampaignContext)
  if (!ctx || ctx.status !== 'custom') {
    return null
  }
  return ctx.slug
}
